/* eslint-disable react/button-has-type */
import Ripple from "@Components/Ripple";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";

import { IButton } from "./constants";

import styles from "./Button.module.scss";
import LoaderIcon from "@Icons/LoaderIcon";
import theme from "@Theme/exportColor.module.scss";
import Chevron from "@Icons/Chevron";
import { ButtonHTMLAttributes } from "react";
import useWhitelabel from "hooks/useWhitelabel";

export interface Props {
  variant: IButton["variant"];
  className?: string;
  labelStyles?: string;
  size?: IButton["size"];
  showLoader?: boolean;
  disable?: boolean;
  greyDisabled?: boolean;
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  btnRef?: React.Ref<HTMLButtonElement>;
  type?: "button" | "submit" | "reset" | undefined;
  RightIcon?: React.ElementType<any>;
  color?: string;
  borderColor?: string;
  style?: any;
  LeftIcon?: React.ElementType<any>;
  isDesktop?: boolean;
  hideHoverEffect?: boolean;
  dataTestId?: string;
  id?: string;
  role?: ButtonHTMLAttributes<HTMLButtonElement>["role"];
  isSelected?: boolean;
}

const Button = ({
  variant,
  className,
  labelStyles,
  size,
  showLoader,
  disable,
  children,
  onClick,
  type,
  btnRef,
  RightIcon,
  color,
  borderColor,
  style,
  LeftIcon,
  dataTestId = "",
  id,
  isDesktop = false,
  hideHoverEffect = false,
  role = "button",
  isSelected = false,
  greyDisabled = false,
}: Props): JSX.Element => {
  const disabled = disable || showLoader;
  const { isWhitelabel, whitelabelColors } = useWhitelabel();

  const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    if (onClick) onClick(e);
  };

  const getRotate = () => {
    switch (variant) {
      case "showLess":
        return "180";
      case "showAll":
        return "270";
      default:
        return "";
    }
  };

  const getLoaderVariant = () => {
    switch (variant) {
      case "outline":
        return "#e53863";
      case "subtle":
        return "#e53863";
      default:
        return "#fff";
    }
  };

  return (
    <button
      ref={btnRef}
      disabled={disabled}
      type={type}
      className={formatClassNames(
        styles.btn,
        variant && styles[variant],
        size && styles[size],
        showLoader && styles.showLoader,
        disable && styles.disabled,
        greyDisabled && styles.greyDisabled,
        isDesktop && styles.desktop,
        hideHoverEffect && styles.hideHover,
        className,
      )}
      style={
        color
          ? { ...style, color: theme[color], border: `1px solid ${theme[borderColor || color]}` }
          : { ...style }
      }
      data-testid={dataTestId}
      id={id}
      role={role}
      aria-selected={isSelected}
    >
      {type !== "submit" && <Ripple onClick={!disabled ? handleOnClick : undefined} />}
      <span className={formatClassNames(styles.btnInnerContent, labelStyles)}>
        {showLoader && <LoaderIcon fill={getLoaderVariant()} size={20} />}
        {!showLoader && (
          <>
            {LeftIcon && (
              <LeftIcon {...(isWhitelabel && { fill: whitelabelColors?.brand_color })} />
            )}
            {children}
            {RightIcon && (
              <RightIcon {...(isWhitelabel && { fill: whitelabelColors?.brand_color })} />
            )}
          </>
        )}
        {["showMore", "showLess", "showAll"].includes(variant) && (
          <Chevron height="12" width="12" rotate={getRotate()} />
        )}
      </span>
    </button>
  );
};

Button.defaultProps = {
  className: "",
  size: "",
  showLoader: false,
  disable: false,
  onClick: undefined,
  btnRef: undefined,
  type: "button",
  color: "",
};

export default Button;
